import React, { useState } from 'react';
import { SxProps, TextField, Theme } from '@mui/material';
import { formatDateMMDDYYYY } from '../helpers/utilityFunctions';
import { formatDateForDisplay, parseDate, isDateInRange } from '../helpers/dateInputUtils';

interface DateTextFieldProps {
  value: string | null;
  onChange: (isoValue: string | null) => void;
  onBlur?: () => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  error?: string;
}

const DateTextField: React.FC<DateTextFieldProps> = ({
  value,
  onChange,
  onBlur,
  disabled,
  sx,
  error,
}) => {
  const [dateText, setDateText] = useState<string | null>(null);
  const [dateError, setDateError] = useState(false);

  const displayedText = dateText ?? (value ? formatDateMMDDYYYY(value) : '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDateForDisplay(e.target.value);
    setDateText(formatted);
    setDateError(false);
    const parsed = parseDate(formatted);
    const valid = parsed.isValid() && isDateInRange(parsed);
    onChange(valid ? parsed.toISOString() : null);
  };

  const handleBlur = () => {
    onBlur?.();
    if (!displayedText) {
      setDateError(false);
      return;
    }
    const parsed = parseDate(displayedText);
    if (parsed.isValid() && isDateInRange(parsed)) {
      setDateText(null);
      onChange(parsed.toISOString());
    } else {
      setDateError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const hasError = dateError || Boolean(error);
  const errorText = dateError ? 'Invalid date' : error;

  return (
    <TextField
      className={`form-input-number${disabled ? ' form-input-number--compact' : ''}`}
      variant='standard'
      placeholder='MM/DD/YYYY'
      sx={sx}
      value={displayedText}
      error={hasError}
      helperText={errorText}
      disabled={disabled}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default DateTextField;
